import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronRight, Eye, GripVertical } from 'lucide-react';

const getColumnKey = (column, index) => {
  if (typeof column?.header === 'string' && column.header.trim()) return column.header.trim();
  if (typeof column?.accessorKey === 'string' && column.accessorKey.trim()) return column.accessorKey.trim();
  if (typeof column?.accessorKey === 'function') return column.header || `col-${index}`;
  return `col-${index}`;
};

const getDefaultColumnWidth = (column, fallback = 180) => {
  const headerText = String(column?.header || column?.accessorKey || '').toLowerCase();

  if (headerText.includes('subject')) return 340;
  if (headerText.includes('remarks')) return 180;
  if (headerText.includes('sender') || headerText.includes('recipient') || headerText.includes('to')) return 150;
  if (headerText.includes('date')) return 110;
  if (headerText.includes('diary') || headerText.includes('dispatch') || headerText.includes('number') || headerText.includes('no.')) return 120;
  if (headerText.includes('priority') || headerText.includes('status')) return 95;

  return Number(column?.width || column?.minWidth || fallback);
};

const Table = ({
  columns,
  data,
  onRowClick,
  sortConfig,
  onSort,
  renderExpandedRow,
  className = '',
  tableClassName = '',
  density = 'comfortable',
  onRowSelect,
  selectedRows,
  enableColumnVisibility = true,
  enableColumnResize = true,
  showColumnControls = true,
  disableTableFilterRow = false,
  onColumnVisibilityChange,
  onColumnResizeChange
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(() => columns.map((column, index) => ({
    key: getColumnKey(column, index),
    visible: column.visible !== false
  })));
  const [columnWidths, setColumnWidths] = useState(() => Object.fromEntries(
    columns.map((column, index) => [getColumnKey(column, index), getDefaultColumnWidth(column, 180)])
  ));
  const columnSignature = useMemo(() => columns.map((column, index) => {
    const key = getColumnKey(column, index);
    return `${key}:${column.visible === false ? 'hidden' : 'visible'}:${column.width || column.minWidth || 180}`;
  }).join('|'), [columns]);
  const resizeStateRef = useRef(null);
  const isCompact = density === 'compact';
  const headCellClass = isCompact
    ? 'px-2 py-2 sm:px-3 sm:py-2 text-left text-[10px] sm:text-[11px]'
    : 'px-3 py-3 sm:px-5 sm:py-3 text-left text-[11px] sm:text-xs';
  const bodyCellClass = isCompact
    ? 'px-2 py-2 sm:px-3 sm:py-2 text-[11px] sm:text-xs'
    : 'px-3 py-2.5 sm:px-5 sm:py-3 text-xs sm:text-sm';
  const miniCellClass = isCompact
    ? 'px-2 py-2 sm:px-3 sm:py-2'
    : 'px-3 py-2.5 sm:px-5 sm:py-3';

  const toggleRow = (e, rowId) => {
    e.stopPropagation();
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (e) => {
    if (onRowSelect) {
      if (e.target.checked) {
        onRowSelect(new Set(data.map(row => row.id)));
      } else {
        onRowSelect(new Set());
      }
    }
  };

  const handleSelectRow = (e, rowId) => {
    e.stopPropagation();
    if (onRowSelect) {
      const newSelected = new Set(selectedRows);
      if (e.target.checked) {
        newSelected.add(rowId);
      } else {
        newSelected.delete(rowId);
      }
      onRowSelect(newSelected);
    }
  };

  const handleHeaderClick = (column) => {
    if (column.sortable && onSort && column.accessorKey) {
      onSort(column.accessorKey);
    }
  };

  const getSortIcon = (column) => {
    if (!column.sortable) return null;

    if (sortConfig?.key === column.accessorKey) {
      return sortConfig.direction === 'asc' ?
        <ChevronUp className="w-4 h-4 ml-1 inline-block" /> :
        <ChevronDown className="w-4 h-4 ml-1 inline-block" />;
    }
    return <ChevronsUpDown className="w-4 h-4 ml-1 inline-block text-gray-300 group-hover:text-gray-500" />;
  };

  useEffect(() => {
    const nextVisibility = columns.map((column, index) => ({
      key: getColumnKey(column, index),
      visible: column.visible !== false
    }));

    setColumnVisibility((current) => {
      const currentMap = Object.fromEntries((current || []).map((item) => [item.key, item.visible]));
      const nextMap = Object.fromEntries(nextVisibility.map((item) => [item.key, item.visible]));
      const hasVisibilityChange = nextVisibility.some((item) => currentMap[item.key] !== item.visible)
        || (current || []).some((item) => nextMap[item.key] === undefined);

      return hasVisibilityChange ? nextVisibility : current;
    });

    setColumnWidths((current) => {
      const nextWidths = { ...current };
      let hasWidthChange = false;

      columns.forEach((column, index) => {
        const key = getColumnKey(column, index);
        const nextDefaultWidth = getDefaultColumnWidth(column, 180);
        const currentWidth = Number(current[key] || 0);

        if (!Number.isFinite(currentWidth) || currentWidth <= 0 || currentWidth < nextDefaultWidth * 0.5) {
          nextWidths[key] = nextDefaultWidth;
          hasWidthChange = true;
        }
      });

      return hasWidthChange ? nextWidths : current;
    });
  }, [columnSignature]);

  useEffect(() => {
    onColumnResizeChange?.(columnWidths);
  }, [columnWidths, onColumnResizeChange]);

  useEffect(() => {
    if (!resizeStateRef.current) return undefined;

    const handleMouseMove = (event) => {
      const { key, startX, startWidth, minWidth } = resizeStateRef.current;
      const diff = event.clientX - startX;
      const nextWidth = Math.max(minWidth, startWidth + diff);
      setColumnWidths((prev) => ({ ...prev, [key]: nextWidth }));
    };

    const handleMouseUp = () => {
      resizeStateRef.current = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizeStateRef]);

  const columnVisibilityLookup = useMemo(() => Object.fromEntries(
    columnVisibility.map((item) => [item.key, item.visible])
  ), [columnVisibility]);

  const visibleColumns = useMemo(() => {
    return columns.filter((column, index) => {
      const key = getColumnKey(column, index);
      return columnVisibilityLookup[key] ?? column.visible !== false;
    });
  }, [columnVisibilityLookup, columns]);

  const totalVisibleWidth = useMemo(() => visibleColumns.reduce((sum, column, index) => {
    const key = getColumnKey(column, index);
    const base = Number(columnWidths[key] || column.width || column.minWidth || 180);
    return sum + Math.max(base, Number(column.minWidth || 100));
  }, 0), [columnWidths, visibleColumns]);

  const renderCell = (column, row) => column.render
    ? column.render(row)
    : typeof column.accessorKey === 'function'
      ? column.accessorKey(row)
      : row[column.accessorKey];

  const toggleColumnVisibility = (key) => {
    const nextState = columnVisibility.map((column) => {
      if (column.key !== key) return column;
      return { ...column, visible: !column.visible };
    });
    setColumnVisibility(nextState);
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(nextState);
    }
  };

  const beginResize = (event, column, index) => {
    if (!enableColumnResize) return;
    event.preventDefault();
    event.stopPropagation();
    const key = getColumnKey(column, index);
    resizeStateRef.current = {
      key,
      startX: event.clientX,
      startWidth: Number(columnWidths[key] || column.width || column.minWidth || 180),
      minWidth: Number(column.minWidth || 100)
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  return (
    <div className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.05)] ${className}`}>
      {enableColumnVisibility && showColumnControls && visibleColumns.length > 0 && (
        <div className="flex items-center justify-end border-b border-slate-200 bg-slate-50 px-3 py-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowColumnMenu((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
            >
              <Eye className="h-3.5 w-3.5" />
              Columns
            </button>
            {showColumnMenu && (
              <div className="absolute right-0 z-20 mt-2 w-52 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
                {columns.map((column, index) => {
                  const key = getColumnKey(column, index);
                  const isVisible = columnVisibility.find((item) => item.key === key)?.visible ?? column.visible !== false;
                  return (
                    <label key={key} className="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100">
                      <span>{column.header}</span>
                      <input
                        type="checkbox"
                        checked={isVisible}
                        onChange={() => toggleColumnVisibility(key)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="divide-y divide-slate-200 sm:hidden">
        {data.length > 0 ? data.map((row, rowIdx) => {
          const rowKey = row.id || rowIdx;
          return (
            <div
              key={rowKey}
              onClick={() => {
                if (onRowClick) onRowClick(row);
                else if (renderExpandedRow) toggleRow({ stopPropagation: () => {} }, rowKey);
              }}
              className={`p-4 ${onRowClick || renderExpandedRow ? 'cursor-pointer active:bg-indigo-50' : ''} ${selectedRows?.has(row.id) ? 'bg-blue-50' : 'bg-white'}`}
            >
              {(onRowSelect || renderExpandedRow) && (
                <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-3">
                  {onRowSelect ? (
                    <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-600" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={!!selectedRows?.has(row.id)}
                        onChange={(event) => handleSelectRow(event, row.id)}
                      />
                      Select
                    </label>
                  ) : <span />}
                  {renderExpandedRow && (
                    <button
                      type="button"
                      onClick={(event) => toggleRow(event, rowKey)}
                      className="inline-flex items-center gap-1 rounded-md p-1.5 text-xs text-indigo-600 hover:bg-indigo-50"
                    >
                      Details
                      {expandedRows.has(rowKey) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  )}
                </div>
              )}
              <dl className="space-y-3">
                {columns.filter((column) => !column.mobileHidden).map((column, columnIdx) => (
                  <div key={columnIdx} className="grid min-w-0 grid-cols-[7.25rem_minmax(0,1fr)] gap-3">
                    <dt className="text-[10px] font-semibold uppercase text-gray-500 break-words">{column.header || 'Details'}</dt>
                    <dd className="min-w-0 text-sm text-gray-800 break-words [overflow-wrap:anywhere]">{renderCell(column, row)}</dd>
                  </div>
                ))}
              </dl>
              {renderExpandedRow && expandedRows.has(rowKey) && (
                <div className="mt-4 border-t border-gray-100 pt-4">{renderExpandedRow(row)}</div>
              )}
            </div>
          );
        }) : (
          <div className="px-4 py-12 text-center">
            <p className="text-base font-medium text-gray-600">No records found</p>
            <p className="mt-1 text-sm text-gray-400">Try adjusting your search or filters</p>
          </div>
        )}
      </div>
      <div className="hidden overflow-x-auto sm:block">
        <table className={`w-full min-w-0 border-separate border-spacing-0 ${tableClassName}`} style={{ tableLayout: 'fixed' }}>
        <thead className="bg-slate-100/80">
          <tr>
            {onRowSelect && (
              <th scope="col" className={`w-8 ${miniCellClass}`}>
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                  checked={!!(data.length > 0 && selectedRows?.size === data.length)}
                  onChange={handleSelectAll}
                />
              </th>
            )}
            {renderExpandedRow && <th scope="col" className={`w-8 ${miniCellClass}`}></th>}
            {visibleColumns.map((col, idx) => {
              const key = getColumnKey(col, idx);
              const width = Math.max(Number(columnWidths[key] || col.width || col.minWidth || 180), Number(col.minWidth || 100));
              const widthPercent = totalVisibleWidth > 0 ? (width / totalVisibleWidth) * 100 : 100 / visibleColumns.length;
              return (
                <th
                  key={key}
                  scope="col"
                  onClick={() => col.sortable && onSort && onSort(col.accessorKey)}
                  className={`
                    ${headCellClass} border-b border-slate-200 bg-slate-100/90 font-semibold text-slate-600 uppercase tracking-[0.12em] whitespace-normal break-words [overflow-wrap:anywhere]
                    ${col.className || ''}
                    last:sticky last:right-0 last:z-10 last:bg-slate-100/90
                    ${col.sortable ? 'cursor-pointer hover:bg-slate-200 hover:text-indigo-600 transition-colors select-none group' : ''}
                  `}
                  style={{ width: `${widthPercent}%`, minWidth: col.minWidth || 100 }}
                >
                  <div className="relative flex items-center gap-2">
                    <span>{col.header}</span>
                    {col.sortable && (
                      <span className="inline-flex text-gray-400 group-hover:text-indigo-500">
                        {sortConfig?.key === col.accessorKey ? (
                          sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronsUpDown className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </span>
                    )}
                    {enableColumnResize && (
                      <button
                        type="button"
                        onMouseDown={(event) => beginResize(event, col, idx)}
                        onClick={(event) => event.stopPropagation()}
                        className="absolute -right-1 top-0 flex h-full w-4 cursor-col-resize items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-200/80 hover:text-slate-600"
                        aria-label={`Resize ${col.header} column`}
                      >
                        <GripVertical className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
          {!disableTableFilterRow && visibleColumns.length > 0 && (
            <tr className="bg-slate-50/70">
              {onRowSelect && <th className="px-2 py-2 text-left" />}
              {renderExpandedRow && <th className="px-2 py-2 text-left" />}
              {visibleColumns.map((col, idx) => (
                <th key={`filter-${getColumnKey(col, idx)}`} className="px-2 py-2 align-top">
                  {col.filterable ? (
                    <input
                      type="text"
                      value={col.filterValue || ''}
                      onChange={(event) => col.onFilterChange?.(event.target.value)}
                      className="w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-700 outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200"
                      placeholder={`Filter ${col.header}`}
                    />
                  ) : (
                    <span className="block h-7" />
                  )}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody className="divide-y divide-slate-200 bg-white">
          {data.length > 0 ? (
            data.map((row, rowIdx) => (
              <React.Fragment key={row.id || rowIdx}>
                <tr 
                  onClick={() => {
                    if (onRowClick) onRowClick(row);
                    else if (renderExpandedRow) toggleRow({ stopPropagation: () => {} }, row.id || rowIdx);
                  }}
                  className={`
                    transition-colors duration-150 ease-in-out border-b border-slate-200
                    ${(onRowClick || renderExpandedRow) ? 'cursor-pointer hover:bg-indigo-50/50' : 'hover:bg-slate-50'}
                    ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}
                    ${expandedRows.has(row.id || rowIdx) ? 'bg-indigo-50/70' : ''}
                    ${selectedRows?.has(row.id) ? 'bg-blue-50/80' : ''}
                  `}
                >
                  {onRowSelect && (
                    <td className={`${miniCellClass} whitespace-nowrap w-8`} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                        checked={!!selectedRows?.has(row.id)}
                        onChange={(e) => handleSelectRow(e, row.id)}
                      />
                    </td>
                  )}
                  {renderExpandedRow && (
                    <td className={`${miniCellClass} whitespace-nowrap text-xs text-gray-500 w-8`} onClick={(e) => e.stopPropagation()}>
                      <button 
                        onClick={(e) => toggleRow(e, row.id || rowIdx)}
                        className="p-1 hover:bg-indigo-100 rounded-full transition-colors"
                      >
                        {expandedRows.has(row.id || rowIdx) ? 
                          <ChevronDown className="w-4 h-4 text-indigo-600" /> : 
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        }
                      </button>
                    </td>
                  )}
                  {visibleColumns.map((col, colIdx) => {
                    const key = getColumnKey(col, colIdx);
                    const width = Math.max(Number(columnWidths[key] || col.width || col.minWidth || 180), Number(col.minWidth || 100));
                    const widthPercent = totalVisibleWidth > 0 ? (width / totalVisibleWidth) * 100 : 100 / visibleColumns.length;
                    return (
                      <td key={key} className={`${bodyCellClass} border-b border-slate-200 text-gray-700 align-top ${col.noWrap === true ? 'whitespace-nowrap' : 'whitespace-normal break-words [overflow-wrap:anywhere]'} ${col.cellClassName || ''} last:sticky last:right-0 last:z-[1] last:bg-white`} style={{ width: `${Math.max(widthPercent, 12)}%`, minWidth: col.minWidth || 100 }}>
                        {renderCell(col, row)}
                      </td>
                    );
                  })}
                </tr>
                {renderExpandedRow && expandedRows.has(row.id || rowIdx) && (
                  <tr className="bg-gray-50/50">
                    <td colSpan={visibleColumns.length + (renderExpandedRow ? 1 : 0) + (onRowSelect ? 1 : 0)} className={`${miniCellClass} border-b border-gray-100 animate-in fade-in slide-in-from-top-1 duration-200`}>
                      {renderExpandedRow(row)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td colSpan={visibleColumns.length + (renderExpandedRow ? 1 : 0) + (onRowSelect ? 1 : 0)} className="px-3 py-10 sm:px-6 sm:py-16 text-center text-gray-500 bg-gray-50/50">
                <div className="flex flex-col items-center justify-center">
                  <p className="text-base font-medium text-gray-600">No records found</p>
                  <p className="mt-1 text-sm text-gray-400">Try adjusting your search or filters</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default Table;
